import React, { useState } from 'react';
import {
  Container,
  Grid,
  Typography,
  Box,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  Divider,
  Chip,
  IconButton,
  Tooltip,
  Drawer,
  useMediaQuery,
  useTheme
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { Share as ShareIcon, Menu as MenuIcon } from '@mui/icons-material';
import styled from 'styled-components';

const BlogContainer = styled(Container)`
  padding: 2rem 0;
`;

const BlogContent = styled(Box)`
  padding: 1.5rem;
  background: ${({ theme }) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff'};
  border-radius: 8px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.05);
  height: 100%;
`;

const BlogHeader = styled(Box)`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
`;

const BlogTitleArea = styled(Box)`
  flex: 1;
`;

const BlogMeta = styled(Box)`
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-top: 0.75rem;
`;

const DateText = styled(Typography)`
  color: ${({ theme }) => theme.palette.text.secondary};
  font-size: 0.875rem;
  display: flex;
  align-items: center;
`;

const ShareButton = styled(IconButton)(({ theme }) => ({
  color: theme.palette.primary.main,
  backgroundColor: theme.palette.action.hover,
  padding: '6px',
  marginLeft: '4px',
  '&:hover': {
    backgroundColor: theme.palette.action.selected,
  },
  '& .MuiSvgIcon-root': {
    fontSize: '1.25rem',
  }
}));

const BlogCategory = styled(Chip)`
  margin-top: 0.75rem;
  height: 24px;
  font-size: 0.875rem;
`;

const RightMenuContainer = styled(Box)`
  background: ${({ theme }) => theme.palette.mode === 'dark' ? theme.palette.background.paper : '#fff'};
  border-radius: 8px;
  box-shadow: ${({ theme }) => theme.palette.mode === 'dark' 
    ? '0 2px 8px rgba(0, 0, 0, 0.3)' 
    : '0 2px 8px rgba(0, 0, 0, 0.05)'};
  padding: 1rem;
  height: 100%;
`;

const StyledList = styled(List)`
  padding: 0;
  position: sticky;
  top: 0;
  max-height: calc(100vh - 4rem);
  overflow-y: auto;

  /* Scrollbar styling for dark theme */
  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: ${({ theme }) => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : theme.palette.grey[100]};
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: ${({ theme }) => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.1)' 
      : theme.palette.grey[400]};
    border-radius: 4px;
  }
`;

const StyledListItem = styled(ListItem)`
  padding: 0;
  margin-bottom: 0.25rem;

  &:last-child {
    margin-bottom: 0;
  }
`;

const StyledListItemButton = styled(ListItemButton)`
  border-radius: 4px;
  padding: 0.5rem;
  min-height: 40px;
  display: flex;
  justify-content: space-between;
  align-items: center;
  
  &:hover {
    background-color: ${({ theme }) => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.05)' 
      : theme.palette.grey[100]};
  }

  &.Mui-selected {
    background-color: ${({ theme }) => theme.palette.mode === 'dark' 
      ? 'rgba(255, 255, 255, 0.08)' 
      : `${theme.palette.primary.light}15`};
    
    &:hover {
      background-color: ${({ theme }) => theme.palette.mode === 'dark' 
        ? 'rgba(255, 255, 255, 0.12)' 
        : `${theme.palette.primary.light}25`};
    }
  }
`;

const BlogListItemTitle = styled(Typography)`
  font-weight: 500;
  color: ${({ theme }) => theme.palette.mode === 'dark' 
    ? theme.palette.text.primary 
    : theme.palette.text.primary};
  font-size: 0.875rem;
  line-height: 1.2;
  
  &.Mui-selected {
    color: ${({ theme }) => theme.palette.primary.main};
  }
`;

const MobileMenuButton = styled(IconButton)`
  position: fixed;
  bottom: 2rem;
  right: 2rem;
  background: ${({ theme }) => theme.palette.primary.main};
  color: white;
  z-index: 1000;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.2);
  
  &:hover {
    background: ${({ theme }) => theme.palette.primary.dark};
  }
`;

export interface BlogPost {
  id: string;
  title: string;
  date: string;
  category: string;
}

interface BlogLayoutProps {
  posts: BlogPost[];
  selectedPostId: string;
  children: React.ReactElement<{ renderHeader?: (title: string, date: string, category?: string) => React.ReactNode }>;
}

const BlogLayout: React.FC<BlogLayoutProps> = ({ posts, selectedPostId, children }) => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [mobileOpen, setMobileOpen] = useState(false);

  const handleShare = async () => {
    const url = window.location.href;
    try {
      if (navigator.share) {
        await navigator.share({
          title: document.title,
          url: url
        });
      } else {
        await navigator.clipboard.writeText(url);
      }
    } catch (error) {
      console.error('Error sharing:', error);
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const drawer = (
    <Box
      sx={{
        backgroundColor: 'background.paper',
        borderRadius: 1,
        boxShadow: 1,
        p: 2,
        height: '100%'
      }}
    >
      <List
        sx={{
          p: 0,
          position: 'sticky',
          top: 0,
          maxHeight: 'calc(100vh - 4rem)',
          overflowY: 'auto',
          '&::-webkit-scrollbar': {
            width: '8px'
          },
          '&::-webkit-scrollbar-track': {
            backgroundColor: 'action.hover',
            borderRadius: '4px'
          },
          '&::-webkit-scrollbar-thumb': {
            backgroundColor: 'action.selected',
            borderRadius: '4px'
          }
        }}
      >
        {posts.map((post) => (
          <ListItem key={post.id} disablePadding sx={{ mb: 0.5, '&:last-child': { mb: 0 } }}>
            <ListItemButton
              selected={selectedPostId === post.id}
              onClick={() => {
                navigate(`/blog/${post.id}`);
                if (isMobile) handleDrawerToggle();
              }}
              sx={{
                borderRadius: 1,
                p: 1,
                minHeight: '40px',
                '&.Mui-selected': {
                  backgroundColor: 'action.selected',
                  '&:hover': {
                    backgroundColor: 'action.focus'
                  }
                },
                '&:hover': {
                  backgroundColor: 'action.hover'
                }
              }}
            >
              <Typography
                variant="subtitle1"
                sx={{
                  fontWeight: 500,
                  fontSize: '0.875rem',
                  lineHeight: 1.2,
                  color: 'text.primary',
                  '&.Mui-selected': {
                    color: 'primary.main'
                  }
                }}
              >
                {post.title}
              </Typography>
            </ListItemButton>
          </ListItem>
        ))}
      </List>
    </Box>
  );

  return (
    <BlogContainer maxWidth="lg">
      <Grid 
        container 
        spacing={3} 
        alignItems="flex-start"
        sx={{
          margin: 0,
          width: '100%'
        }}
      >
        {/* Blog Content Column */}
        <Grid 
          item 
          xs={12} 
          md={9} 
          order={{ xs: 2, md: 1 }}
          sx={{ 
            padding: '0 1.5rem 0 0 !important',
            '@media (max-width: 900px)': {
              padding: '0 !important'
            }
          }}
        >
          <Box
            sx={{
              backgroundColor: 'background.paper',
              borderRadius: 1,
              boxShadow: 1,
              p: 3,
              height: '100%'
            }}
          >
            {React.isValidElement(children) &&
              React.cloneElement(children, {
                renderHeader: (title: string, date: string, category?: string) => (
                  <BlogHeader>
                    <BlogTitleArea>
                      <Typography variant="h4" component="h1" gutterBottom>
                        {title}
                      </Typography>
                      <BlogMeta>
                        <DateText>
                          {date}
                        </DateText>
                        <IconButton
                          onClick={handleShare}
                          size="small"
                          sx={{
                            padding: '4px',
                            color: 'primary.main',
                            backgroundColor: 'action.hover',
                            '&:hover': {
                              backgroundColor: 'action.selected'
                            }
                          }}
                        >
                          <ShareIcon fontSize="small" />
                        </IconButton>
                      </BlogMeta>
                      {category && (
                        <BlogCategory 
                          label={category} 
                          color="primary"
                        />
                      )}
                    </BlogTitleArea>
                  </BlogHeader>
                )
              })
            }
          </Box>
        </Grid>

        {/* Blog List Column - Desktop */}
        {!isMobile && (
          <Grid 
            item 
            xs={12} 
            md={3} 
            order={{ xs: 1, md: 2 }}
            sx={{ 
              padding: '0 !important'
            }}
          >
            {drawer}
          </Grid>
        )}

        {/* Mobile Menu Button */}
        {isMobile && (
          <MobileMenuButton
            color="primary"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
          >
            <MenuIcon />
          </MobileMenuButton>
        )}

        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          anchor="right"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{
            keepMounted: true,
          }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': { 
              boxSizing: 'border-box', 
              width: '85%',
              maxWidth: '320px'
            },
          }}
        >
          {drawer}
        </Drawer>
      </Grid>
    </BlogContainer>
  );
};

export default BlogLayout; 